import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sun, CheckCircle2, AlertTriangle, Mail, Building2, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, checkAuth, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/invites/${token}`);
        setInfo(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Einladung nicht gefunden");
      }
    })();
  }, [token]);

  // Erwartete vs eingeloggte E-Mail
  const inv = info?.invite;
  const expectedEmail = (inv?.email || "").toLowerCase();
  const currentEmail = (user?.email || "").toLowerCase();
  const isEmailMismatch = !!(user && expectedEmail && currentEmail && currentEmail !== expectedEmail);

  // (a) Bessere Fehlermeldungen — User-freundliche Übersetzung des Backend-Details
  const friendlyError = useMemo(() => {
    if (!error) return "";
    const lower = String(error).toLowerCase();
    if (lower.includes("not authenticated") || lower.includes("unauthorized") || lower === "not authenticated") {
      if (isEmailMismatch) {
        return "Diese Einladung gehört zu einer anderen E-Mail-Adresse. Bitte abmelden und mit der eingeladenen Adresse neu anmelden.";
      }
      return "Bitte melden Sie sich zuerst an, um die Einladung anzunehmen.";
    }
    if (lower.includes("invitation_email_mismatch") || lower.includes("invite_email_mismatch") || lower.includes("e-mail stimmt nicht")) {
      return "Diese Einladung gehört zu einer anderen E-Mail-Adresse. Bitte abmelden und mit der eingeladenen Adresse neu anmelden.";
    }
    return error;
  }, [error, isEmailMismatch]);

  const accept = async () => {
    if (!user) {
      // Login-Flow starten
      window.location.href = `/?next=/invite/${token}`;
      return;
    }
    // (b) Strikter Check — wir lassen den POST gar nicht erst raus
    if (isEmailMismatch) {
      setError("invite_email_mismatch");
      return;
    }
    setAccepting(true);
    setError("");
    try {
      const r = await api.post(`/invites/${token}/accept`);
      toast.success(`Sie sind jetzt Teil von ${r.data.company_name}`);
      await checkAuth();
      nav("/dashboard");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Annahme fehlgeschlagen";
      setError(detail);
      toast.error(typeof detail === "string" ? detail : "Annahme fehlgeschlagen");
    } finally { setAccepting(false); }
  };

  const logoutAndKeepInvite = async () => {
    setLoggingOut(true);
    try {
      // Damit der User nach dem Re-Login direkt wieder hier landet
      try { sessionStorage.setItem("pending_invite_token", token); } catch (_) { /* noop */ }
      window.location.href = `/?next=/invite/${token}&logout=1`;
      // Backend-Logout im Hintergrund
      try { await logout(); } catch (_) { /* noop */ }
    } finally { setLoggingOut(false); }
  };

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md p-8 text-center border-red-200" data-testid="invite-error-card">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Einladung nicht verfügbar</h1>
          <p className="text-sm text-slate-600">{friendlyError}</p>
        </Card>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen flex items-center justify-center text-slate-500">Wird geladen …</div>;

  const primary = info.company_primary_color || "#f97316";
  const expired = inv.status === "expired";
  const accepted = inv.status === "accepted";
  const revoked = inv.status === "revoked";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="invite-accept-page">
      <Card className="max-w-md w-full p-8 space-y-5 border-slate-200">
        <div className="flex items-center gap-3">
          {info.company_logo ? (
            <img src={info.company_logo} alt="" className="h-12 max-w-[140px] object-contain bg-white rounded border border-slate-200 p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: primary }}><Sun className="w-5 h-5 text-white" /></div>
          )}
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Einladung von</p>
            <p className="font-bold text-slate-900">{info.agency_name}</p>
          </div>
        </div>

        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Sie wurden eingeladen
          </h1>
          <p className="text-slate-600 text-sm">
            Werden Sie Teil von <span className="font-semibold">{info.company_name}</span> als <span className="font-semibold">{inv.role === "client_admin" ? "Admin" : "Mitarbeiter"}</span>.
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm space-y-1">
          <div className="flex items-center gap-2 text-slate-700"><Mail className="w-3 h-3" /> {inv.email}</div>
          <div className="flex items-center gap-2 text-slate-700"><Building2 className="w-3 h-3" /> {info.company_name}</div>
        </div>

        {expired && (
          <div data-testid="invite-expired" className="text-amber-700 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Diese Einladung ist abgelaufen.</div>
        )}
        {accepted && (
          <div data-testid="invite-accepted" className="text-emerald-700 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Diese Einladung wurde bereits angenommen.</div>
        )}
        {revoked && (
          <div data-testid="invite-revoked" className="text-red-700 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Diese Einladung wurde widerrufen.</div>
        )}

        {!expired && !accepted && !revoked && (
          <>
            {isEmailMismatch ? (
              <div data-testid="invite-email-mismatch" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 space-y-3">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-900 leading-relaxed">
                    Diese Einladung gehört zu <span className="font-bold">{inv.email}</span>. Sie sind aber als <span className="font-bold">{user.email}</span> angemeldet.
                    <div className="mt-1.5 text-xs text-amber-800">
                      Melden Sie sich zuerst ab und dann mit der eingeladenen Adresse neu an, um die Einladung anzunehmen.
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="invite-logout-btn"
                  onClick={logoutAndKeepInvite}
                  disabled={loggingOut}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {loggingOut ? "Wird abgemeldet …" : "Jetzt abmelden & neu anmelden"}
                </Button>
              </div>
            ) : null}
            <Button
              data-testid="accept-invite-btn"
              onClick={accept}
              disabled={accepting || isEmailMismatch}
              title={isEmailMismatch ? "Mit falscher E-Mail angemeldet" : undefined}
              className="w-full text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: primary }}
            >
              {accepting
                ? "Wird angenommen …"
                : !user
                ? "Mit Google anmelden & annehmen"
                : isEmailMismatch
                ? "Falsche E-Mail-Adresse"
                : "Einladung annehmen"}
            </Button>
            {friendlyError && !isEmailMismatch && (
              <p data-testid="invite-error-message" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {friendlyError}
              </p>
            )}
          </>
        )}

        <p className="text-[11px] text-slate-400 text-center pt-2">Powered by <span className="font-semibold">revenioo.ai</span></p>
      </Card>
    </div>
  );
}
